import React, { useRef, useState } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import {
    motion,
    useScroll,
    useTransform,
    useSpring,
    useVelocity,
    useAnimationFrame,
    AnimatePresence
} from 'framer-motion';
import {
    FaTwitter, FaInstagram, FaGlobe, FaMapMarkerAlt,
    FaUser, FaStar, FaGraduationCap, FaAward, FaChalkboardTeacher, FaQuoteLeft
} from 'react-icons/fa';
import { FiArrowRight, FiCheckCircle } from 'react-icons/fi';

const StatCard = ({ icon: Icon, title, subtitle, active, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.1, duration: 0.5 }}
        whileHover={{ y: -5, boxShadow: "0 20px 40px -10px rgba(59, 130, 246, 0.2)" }}
        className={`
            relative p-6 rounded-2xl flex flex-col items-center justify-center text-center gap-4 transition-all duration-300 border backdrop-blur-sm
            ${active
                ? 'bg-gradient-to-br from-blue-600 to-blue-800 text-white border-blue-500/50 shadow-lg shadow-blue-500/20'
                : 'bg-white/[0.03] border-white/[0.1] hover:border-blue-500/30 hover:bg-white/[0.05] group'
            }
        `}
    >
        <div className={`
            w-12 h-12 rounded-xl flex items-center justify-center text-2xl mb-2 transition-transform duration-300 group-hover:scale-110
            ${active ? 'bg-white/20 text-white' : 'bg-blue-500/10 text-blue-400 group-hover:bg-blue-500 group-hover:text-white'}
        `}>
            <Icon />
        </div>
        <div>
            <h4 className={`font-bold text-lg mb-1 leading-tight ${active ? 'text-white' : 'text-white group-hover:text-blue-400'}`}>{title}</h4>
            <p className={`text-xs uppercase tracking-wider font-medium ${active ? 'text-blue-100' : 'text-neutral-500 group-hover:text-neutral-300'}`}>{subtitle}</p>
        </div>
    </motion.div>
);

const LocationItem = ({ name, location, index }) => (
    <motion.div
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.1 }}
        className="flex items-center gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/[0.05] hover:bg-white/[0.05] hover:border-blue-500/30 transition-all group"
    >
        <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-400 group-hover:bg-blue-500 group-hover:text-white transition-colors">
            <FaMapMarkerAlt />
        </div>
        <div>
            <h5 className="text-white font-bold">{name}</h5>
            <p className="text-sm text-neutral-500 group-hover:text-blue-400 transition-colors">{location}</p>
        </div>
    </motion.div>
);

const VelocityText = ({ children, baseVelocity = 100 }) => {
    const baseX = useSpring(0, { damping: 20, stiffness: 100, restDelta: 0.001 });
    const { scrollY } = useScroll();
    const scrollVelocity = useVelocity(scrollY);
    const smoothVelocity = useSpring(scrollVelocity, {
        damping: 50,
        stiffness: 400
    });
    const velocityFactor = useTransform(smoothVelocity, [0, 1000], [0, 5], {
        clamp: false
    });

    const x = useTransform(baseX, (v) => `${wrap(-20, -45, v)}%`);

    const directionFactor = useRef(1);
    useAnimationFrame((t, delta) => {
        let moveBy = directionFactor.current * baseVelocity * (delta / 1000);

        if (velocityFactor.get() < 0) {
            directionFactor.current = -1;
        } else if (velocityFactor.get() > 0) {
            directionFactor.current = 1;
        }

        moveBy += directionFactor.current * moveBy * velocityFactor.get();

        baseX.set(baseX.get() + moveBy);
    });

    return (
        <div className="flex whitespace-nowrap overflow-hidden">
            <motion.div style={{ x }} className="flex gap-12 whitespace-nowrap">
                {[...Array(6)].map((_, i) => (
                    <div key={i} className="flex items-center gap-8 opacity-40">
                        {children}
                    </div>
                ))}
            </motion.div>
        </div>
    );
};

const PerspectiveMarquee = ({ children, direction = 1, speed = 20, rotate = "5deg" }) => {
    const baseX = useSpring(0, { damping: 20, stiffness: 100, restDelta: 0.001 });

    useAnimationFrame((t, delta) => {
        let moveBy = direction * speed * (delta / 1000);
        baseX.set(baseX.get() + moveBy);
    });

    const x = useTransform(baseX, (v) => `${wrap(-20, -45, v)}%`);

    return (
        <div
            className="flex whitespace-nowrap overflow-hidden"
            style={{
                perspective: "1000px",
                transform: `rotateX(15deg) rotateY(${rotate})`
            }}
        >
            <motion.div
                style={{ x }}
                className="flex gap-12 whitespace-nowrap py-10"
            >
                {[...Array(6)].map((_, i) => (
                    <div key={i} className="flex items-center gap-8 group">
                        <div className="relative">
                            {/* Holographic Ghosting Effect */}
                            <div className="absolute inset-0 blur-sm opacity-30 group-hover:opacity-60 transition-opacity">
                                {children}
                            </div>
                            <div className="relative z-10">
                                {children}
                            </div>
                        </div>
                    </div>
                ))}
            </motion.div>
        </div>
    );
};

// Helper to wrap values
const wrap = (min, max, v) => {
    const rangeSize = max - min;
    return ((((v - min) % rangeSize) + rangeSize) % rangeSize) + min;
};

export default function AboutUs() {
    const containerRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start start", "end end"]
    });

    const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
    const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

    return (
        <div ref={containerRef} className="min-h-screen bg-[#050505] font-sans selection:bg-blue-500/30 selection:text-blue-200 overflow-hidden">
            <Navbar />

            {/* Background Effects */}
            <div className="fixed inset-0 pointer-events-none z-0">
                <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[120px] mix-blend-screen animate-pulse" style={{ animationDuration: '8s' }}></div>
                <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-indigo-600/10 rounded-full blur-[100px] mix-blend-screen animate-pulse" style={{ animationDuration: '10s' }}></div>
                <div className="absolute inset-0 bg-[url('/noise.svg')] opacity-20 brightness-100 contrast-150"></div>
            </div>

            <div className="pt-32 pb-20 relative z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                    {/* Hero Section */}
                    <div className="grid lg:grid-cols-2 gap-16 items-center mb-32">
                        {/* Left Content */}
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                            className="space-y-8 relative"
                        >
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-full text-sm font-bold uppercase tracking-widest backdrop-blur-md">
                                <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
                                About The Tutor
                            </div>

                            <div className="relative">
                                <h1 className="text-5xl md:text-7xl font-black text-white leading-tight tracking-tight">
                                    Manoj <br />
                                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500">Sampath</span>
                                </h1>
                                <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: 100 }}
                                    transition={{ delay: 0.5, duration: 0.8 }}
                                    className="h-2 bg-blue-500 mt-4 rounded-full"
                                />
                            </div>

                            <p className="text-xl text-neutral-400 font-light leading-relaxed max-w-xl border-l-4 border-blue-500/30 pl-6">
                                Mathematics Tutor | Grades 9–11
                            </p>

                            <div className="text-neutral-400 leading-relaxed text-base space-y-6">
                                <p>
                                    I am an experienced and committed Mathematics tutor with over <span className="text-white font-bold">8 years</span> of teaching experience, specializing in Grades 9 to 11.
                                </p>
                                <p>
                                    I completed my G.C.E. Ordinary Level and Advanced Level examinations in the Mathematics stream (English Medium) at <span className="text-blue-400 font-semibold">Bandaranayaka College - Gampaha</span> and later continued my higher studies at the <span className="text-blue-400 font-semibold">University of Moratuwa</span>.
                                </p>
                            </div>

                            <div className="flex flex-wrap gap-4 pt-4">
                                <button onClick={() => window.location.href = '/contact'} className="px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-all shadow-lg shadow-blue-600/20 flex items-center gap-3 group">
                                    Contact Me <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
                                </button>
                                <button className="px-8 py-4 bg-white/5 hover:bg-white/10 text-white border border-white/10 rounded-xl font-bold transition-all flex items-center gap-3">
                                    View Results
                                </button>
                            </div>
                        </motion.div>

                        {/* Right Content - Image */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, rotate: 5 }}
                            animate={{ opacity: 1, scale: 1, rotate: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="relative h-full min-h-[500px] flex items-center justify-center"
                        >
                            <div className="relative w-full max-w-md mx-auto">
                                {/* Decorative Ring */}
                                <div className="absolute inset-0 border-2 border-dashed border-blue-500/30 rounded-full animate-[spin_20s_linear_infinite]"></div>

                                {/* Image Wrapper */}
                                <div className="relative z-10 rounded-[3rem] overflow-hidden border-4 border-white/5 bg-gradient-to-b from-white/5 to-transparent backdrop-blur-sm p-4">
                                    <div className="rounded-[2.5rem] overflow-hidden relative">
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent z-20 opacity-80"></div>
                                        <img
                                            src="/hero/about.png"
                                            alt="Manoj Sampath"
                                            className="w-full h-auto object-cover transform transition-transform duration-700 hover:scale-105"
                                        />
                                    </div>

                                    {/* Floating Badges */}
                                    <motion.div
                                        animate={{ y: [0, -10, 0] }}
                                        transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                                        className="absolute bottom-12 left-8 z-30 bg-white/10 backdrop-blur-xl border border-white/20 p-4 rounded-2xl shadow-xl"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white">
                                                <FaGraduationCap />
                                            </div>
                                            <div>
                                                <p className="text-xs text-blue-200 font-bold uppercase">Alumni</p>
                                                <p className="text-white font-bold">U. of Moratuwa</p>
                                            </div>
                                        </div>
                                    </motion.div>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-32">
                        {[
                            { icon: FaStar, title: "8+ Years", subtitle: "Teaching Exp.", active: true },
                            { icon: FaGraduationCap, title: "English Medium", subtitle: "Specialized", active: false },
                            { icon: FaUser, title: "Grades 9-11", subtitle: "Target Group", active: false },
                            { icon: FaAward, title: "Best Results", subtitle: "Gampaha Region", active: false },
                            { icon: FaChalkboardTeacher, title: "Concept Based", subtitle: "Methodology", active: false },
                        ].map((item, index) => (
                            <StatCard key={index} {...item} index={index} />
                        ))}
                    </div>

                    {/* Philosophy Section */}
                    <div className="grid lg:grid-cols-12 gap-12 mb-32 bg-white/[0.02] border border-white/[0.05] rounded-3xl p-8 lg:p-12 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-bl from-blue-900/10 to-transparent pointer-events-none"></div>

                        <div className="lg:col-span-12 text-center max-w-3xl mx-auto">
                            <FaQuoteLeft className="text-4xl text-blue-500/30 mx-auto mb-6" />
                            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Teaching Philosophy</h2>
                            <p className="text-lg text-neutral-400 leading-relaxed mb-8">
                                "My teaching methodology focuses on concept-based learning, step-by-step explanations, and exam-focused practice, ensuring students gain confidence and long-term mastery in mathematics."
                            </p>

                            <p className="text-neutral-400 leading-relaxed">
                                Throughout my teaching career, I have supported many students in strengthening their mathematical understanding, improving examination results, and developing strong analytical and problem-solving skills. Last year, my students achieved outstanding Mathematics results in the <span className="text-blue-400">Gampaha, Nittambuwa, Kadawatha, Warakapola, Minuwangoda, Sapugaskanda</span> area, marking one of the best performances in the region.
                            </p>
                        </div>
                    </div>

                    {/* Locations Section */}
                    <div className="grid lg:grid-cols-2 gap-12 items-start">
                        <div>
                            <h2 className="text-3xl font-bold text-white mb-2">Class Locations</h2>
                            <p className="text-neutral-500 mb-8">Join our physical classes at these convenient locations.</p>

                            <div className="grid sm:grid-cols-2 gap-4">
                                {[
                                    { name: "Teens", location: "Gampaha" },
                                    { name: "Siyopen", location: "Nittabuwa" },
                                    { name: "Susip", location: "Warakapola" },
                                    { name: "Oxford", location: "Minuwangoda" },
                                    { name: "Shiksha", location: "Sapugaskanda" }
                                ].map((cls, idx) => (
                                    <LocationItem key={idx} {...cls} index={idx} />
                                ))}
                            </div>
                        </div>

                        <div className="relative mt-8 lg:mt-0">
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 blur-2xl opacity-20 transform rotate-3 rounded-3xl"></div>
                            <div className="relative bg-[#0A0A0A] border border-white/10 rounded-3xl p-8 overflow-hidden group">
                                <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
                                    <FaMapMarkerAlt className="text-9xl text-white" />
                                </div>
                                <h3 className="text-2xl font-bold text-white mb-6">Get in Touch</h3>
                                <div className="space-y-6 relative z-10">
                                    <a href="tel:0719304566" className="flex items-center gap-4 text-neutral-400 hover:text-white transition-colors">
                                        <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-blue-500">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
                                        </div>
                                        <div>
                                            <p className="text-xs uppercase tracking-wider font-bold text-blue-500">Call Now</p>
                                            <p className="text-xl font-bold font-mono">071 433 62 94</p>
                                        </div>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Holographic 3D Perspective Marquee Banner */}
                {/* <div className="relative py-32 mt-32 bg-blue-500/5 overflow-hidden">
                    
                    <motion.div
                        animate={{ top: ["0%", "100%", "0%"] }}
                        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                        className="absolute left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-blue-500/50 to-transparent z-30"
                    />

                    <div className="space-y-0">
                        <PerspectiveMarquee speed={30} rotate="-10deg">
                            <span className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-white to-blue-500 uppercase tracking-tighter holographic-text">Manoj Sampath</span>
                             
                        </PerspectiveMarquee>

                        <div className="relative z-20">
                            <PerspectiveMarquee speed={-30} rotate="10deg">
                                <span className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-blue-600 to-white uppercase tracking-tighter outline-text holographic-text">Maths - English Medium</span>
                                
                            </PerspectiveMarquee>
                        </div>
                    </div>

                    
                    <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute top-10 left-10 w-20 h-20 border-t-2 border-l-2 border-blue-500/20"></div>
                        <div className="absolute top-10 right-10 w-20 h-20 border-t-2 border-r-2 border-blue-500/20"></div>
                        <div className="absolute bottom-10 left-10 w-20 h-20 border-b-2 border-l-2 border-blue-500/20"></div>
                        <div className="absolute bottom-10 right-10 w-20 h-20 border-b-2 border-r-2 border-blue-500/20"></div>

                         
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[1px] bg-blue-500/10"></div>
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[80%] w-[1px] bg-blue-500/10"></div>
                    </div>
                </div> */}
            </div>

            <Footer />

            <style jsx>{`
                .outline-text {
                    -webkit-text-stroke: 1px rgba(255, 255, 255, 0.3);
                }
                .holographic-text {
                    text-shadow: 0 0 20px rgba(59, 130, 246, 0.4);
                    filter: drop-shadow(0 0 2px rgba(255, 255, 255, 0.2));
                }
                @keyframes glitch {
                    0% { transform: translate(0); }
                    20% { transform: translate(-2px, 2px); }
                    40% { transform: translate(-2px, -2px); }
                    60% { transform: translate(2px, 2px); }
                    80% { transform: translate(2px, -2px); }
                    100% { transform: translate(0); }
                }
                .holographic-text:hover {
                    animation: glitch 0.3s linear infinite;
                }
            `}</style>
        </div>
    );
}
